<template>
  <div class="tooltip-container" @mouseenter="showTooltip" @mouseleave="hideTooltip">
    <button
      :disabled="!isButtonEnabled || !advisorStore.advisor?.firm?.has_modelsets"
      class="tw-p-3 tw-cursor-pointer tw-bg-white tw-shadow-sm tw-rounded-lg tw-text-[#F1583D] tw-flex tw-items-center tw-font-medium tw-whitespace-nowrap tw-border tw-border-solid tw-border-[#DFE2E7] disabled:tw-bg-[#EEEFF2] disabled:tw-text-[#B3B9C6]"
      @click="loadBestFit"
    >
      <i class="fas fa-arrow-down tw-mr-2" />
      <span v-if="bestfitloading">
        <i class="fa fa-spinner fa-spin tw-text-xl tw-mr-1" />
        Loading Best Fit...
      </span>
      <span v-else>Load best fit</span>
    </button>
    <div v-if="tooltipVisible && !advisorStore.advisor?.firm?.has_modelsets && checkupStore.featureFlags.includes('Feature: Model Matchmaker')" class="tooltip">
        <a href="/advisor-dashboard/configure-modelsets" target="_blank" class="tooltip-link">
          Configure 
        </a>
        which portfolios you want available for load best fit
    </div>
    <div v-else-if="tooltipVisible && checkupStore.featureFlags.includes('Feature: Model Matchmaker')" class="tooltip">
      <p>
        {{ tooltipMessage }}
        <br /><br />
        <a href="/advisor-dashboard/configure-modelsets" target="_blank" class="tooltip-link">
          Configure 
        </a>
        portfolios available for load best fit
        <br /><br />
        For more details, check our
        <a href="https://help.yourstake.org/migration/knowledge/best-fit-portfolio-guide-and-faq" target="_blank" class="tooltip-link">
          help article
        </a>
      </p>
    </div>
    <div v-else-if="tooltipVisible" class="tooltip">
      <p>
        {{ tooltipMessage }}
        <br />
        For more details, check our
        <a href="https://help.yourstake.org/migration/knowledge/best-fit-portfolio-guide-and-faq" target="_blank" class="tooltip-link">
          help article
        </a>
      </p>
    </div>
  </div>
</template>

<script>
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
import { mapStores } from 'pinia';

import { awaitTask } from '@frontend/ui/features/checkup/helpers/tasks';

export default {
  props: {
    selectedModelSets: {
      type: Array,
      default: () => []
    },
    selectedStrategies: {
      type: Array,
      default: () => []
    },
  },
  data() {
    return {
      bestfitloading: false,
      tooltipVisible: false
    };
  },
  computed: {
    ...mapStores(useCheckupStore, useAdvisorStore),
    isButtonEnabled() {
      if (this.checkupStore.clientRisk?.value) {
        return true;
      }
      if (this.checkupStore.selectedPortfolios.length === 1) {
        const portfolio = this.checkupStore.selectedPortfolios[0];
        if ('uploaded_df_agg' in portfolio && portfolio.uploaded_df_agg.length > 0) {
          for (const row of portfolio.uploaded_df_agg) {
            if (row.found === true) {
              return true;
            }
          }
          return false;
        } else {
          return true;
        }
      }
      return false;
    },
    tooltipMessage() {
      if (this.checkupStore.clientRisk?.value || this.checkupStore.selectedPortfolios.length === 1) {
        return 'Finds closest match model portfolio.';
      }
      if (!this.checkupStore.clientRisk?.value && this.checkupStore.selectedPortfolios.length === 0) {
        return 'Select a portfolio or risk level to load best fit';
      }
      if (!this.checkupStore.clientRisk?.value && this.checkupStore.selectedPortfolios.length > 1) {
        return 'Can only run best fit on one portfolio. Click \'Run Analysis\' and refresh the page to find best fit on the combined portfolio';
      }
      if (this.checkupStore.selectedPortfolios.length === 1) {
        const portfolio = this.checkupStore.selectedPortfolios[0];
        if ('uploaded_df_agg' in portfolio && portfolio.uploaded_df_agg.length > 0) {
          for (const row of portfolio.uploaded_df_agg) {
            if (row.found === true) {
              return 'If a risk preference is selected, then the best fit is based on risk similarity. If a risk preference is NOT selected, and a portfolio selected, then the best fit is based on portfolio similarity';
            }
          }
        } else {
          return 'If a risk preference is selected, then the best fit is based on risk similarity. If a risk preference is NOT selected, and a portfolio selected, then the best fit is based on portfolio similarity';
        }
      }
      return 'Your selected portfolio has no matched holdings -- please assign a proxy ticker or run \'magic ticker fill\' to find a best fit';
    },
  },
  methods: {
    showTooltip() {
      this.tooltipVisible = true;
    },
    hideTooltip() {
      this.tooltipVisible = false;
    },
    downloadCsv(filename, csvString) {
      // turn the CSV string into a Blob, create an object URL, and “click” a hidden link
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    async loadBestFit() {
      this.bestfitloading = true;
      try {
        const baseUrl = '/api/v1/get-best-fit/';
        const url = new URL(baseUrl, window.location.origin);

        if (this.advisorStore.advisor?.firm?.name && this.advisorStore.advisor.firm.name.toLowerCase() === 'sei investments co') {
          url.searchParams.append('firm', 'sei-investments-co');
        }
        if (this.advisorStore.advisor?.firm?.name && this.advisorStore.advisor.firm.name.toLowerCase() === 'mercer advisors') {
          url.searchParams.append('firm', 'mercer-advisors');
        }

        if (this.checkupStore?.clientRisk.value !== undefined && this.checkupStore?.clientRisk.value !== null) {
          url.searchParams.append('risk_number', this.checkupStore.clientRisk.value);
        }
        if (this.checkupStore?.selectedPortfolios?.[0]?.external_id !== null && this.checkupStore?.selectedPortfolios?.[0]?.external_id !== undefined) {
          url.searchParams.append('portfolio_external_id', this.checkupStore.selectedPortfolios[0].external_id);
        }
        if (this.checkupStore?.selectedValues !== undefined && this.checkupStore?.selectedValues !== null && this.checkupStore?.selectedValues.length > 0) {
          url.searchParams.append('values', this.checkupStore.selectedValueIds);
        }
        
        // Add selected model sets to the request
        if (this.selectedModelSets.length > 0) {
          url.searchParams.append('model_sets', this.selectedModelSets.join(','));
          console.log('Including model sets in request:', this.selectedModelSets);
        }
        if (this.selectedStrategies.length > 0) {
          url.searchParams.append('strategies', this.selectedStrategies.join(','));
          console.log('Including strategies in request:', this.selectedStrategies);
        }

        const response = await awaitTask(url);
        const data = response.task.meta;

        if (data.error) {
          this.bestfitloading = false;
          this.$buefy.toast.open({
            duration: 30000,
            message: data.error,
            type: 'is-danger',
          });
          return;
        }

        const optimizedPortfolio = data.best_fit;
        if (this.checkupStore.optimizedPortfolio) {
          this.checkupStore.removePortfolio(this.checkupStore.optimizedPortfolio);
        }
        this.checkupStore.addPortfolio(optimizedPortfolio, 'optimizedPortfolio');

        if (data.sei_sorted_csv) {
          this.downloadCsv('best_fit_funds_details.csv', data.sei_sorted_csv);
        }
        if (data.sei_equities_sorted_csv) {
          this.downloadCsv('best_fit_equities_details.csv', data.sei_equities_sorted_csv);
        }
        if (data.mercer_sorted_csv) {
          this.downloadCsv('best_fit_details.csv', data.mercer_sorted_csv);
        }

        this.bestfitloading = false;
      } catch (error) {
        this.bestfitloading = false;
        console.error('Failed to load best fit:', error);
        this.$buefy.toast.open({
          duration: 30000,
          message: error.message,
          type: 'is-danger',
        });
        throw new Error(error);
      }
    },
  },
};
</script>

<style scoped>
.tooltip-container {
  position: relative;
  display: inline-block;
}

.tooltip {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #fff;
  border: 1px solid #dfe2e7;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 10px;
  z-index: 1000;
  width: 300px;
  border-radius: 5px;
}

.tooltip-link {
  color: #f1583d;
  text-decoration: underline;
}
</style>